import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Paginator } from 'primereact/paginator'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import Button from '../../components/ui/Button'
import MeetingList from './components/MeetingList'
import { useSetBreadcrumb } from '../../app/nav/breadcrumb'
import { useNavigateWithOrigin } from '../../app/nav/originNav'
import { exportGroupMeetings } from './api/meetingApi'
import { useMeetingGroup } from './hooks/useMeetingGroup'
import { useGroupMeetings } from './hooks/useGroupMeetings'
import './meetings.css'

const PAGE_SIZE = 20

/**
 * Bir grubun toplantıları (044): breadcrumb (Toplantılar → Grup) + toplantı listesi. Satıra tıklanınca
 * Toplantı Detay'a gider. Grup yok/silinmiş → liste 404 → boş durum. Rota: /minutes/meeting-minutes/meetings/group/:groupId.
 * Liste yalnız <b>yayınlanmış</b> tutanakları içerir — taslaklar `DraftMeetingsPage`'dedir; durum kolonu eski
 * (durumsuz) kayıtları ayırt etmek için kalır.
 */
export default function GroupMeetingsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Toplantı Detay'a giderken bu grup listesi origin taşınır → detayda "Geri" bu gruba döner.
  const goDetail = useNavigateWithOrigin()
  const { groupId: groupIdParam } = useParams()
  const groupId = groupIdParam ? Number(groupIdParam) : null

  const [page, setPage] = useState(0)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null | undefined>(undefined)
  const sort = sortField ? `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}` : undefined
  const group = useMeetingGroup(groupId)
  const meetings = useGroupMeetings(groupId, page, PAGE_SIZE, sort)
  const total = meetings.data?.page.totalElements ?? 0
  const groupName = group.data?.name ?? ''

  // Header breadcrumb'ına ek adım: Grup (mevcut). "Toplantılar" adımı menüden otomatik gelir.
  useSetBreadcrumb(groupName ? [{ label: groupName }] : [])

  return (
    <div className="page meetings-page">
      <PageHeader
        title={groupName || t('meetings.title')}
        actions={
          <>
            {groupId != null && (
              <ExcelExportButton
                onExport={() => exportGroupMeetings(groupId, sort)}
                disabled={(meetings.data?.content ?? []).length === 0}
              />
            )}
            <Button
              variant="secondary"
              icon="pi pi-arrow-left"
              label={t('meetings.backToGroups')}
              onClick={() => navigate('/minutes/meeting-minutes/meetings')}
            />
          </>
        }
      />
      <MeetingList
        items={meetings.data?.content ?? []}
        loading={meetings.isLoading}
        totalRecords={total}
        onSelect={(id) => goDetail(`/minutes/meeting-minutes/meetings/${id}`)}
        showStatus
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(e) => {
          setSortField(e.sortField)
          setSortOrder(e.sortOrder)
          setPage(0)
        }}
      />
      {total > PAGE_SIZE && (
        <Paginator
          first={page * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={total}
          onPageChange={(e) => setPage(e.page)}
        />
      )}
    </div>
  )
}
